import React, {createContext, useContext, useState, useEffect, useCallback} from 'react';
import {AI_FEATURES_ENABLED} from '../helpers/featureFlags';
import {getAiApiBaseUrl} from '../helpers/aiApiConfig';

const AiAvailabilityContext = createContext({
  aiAvailable: false,
  aiChecking: true,
  refreshAiAvailability: () => {},
});

export const useAiAvailable = () => useContext(AiAvailabilityContext);

async function probeAiService(signal) {
  const baseUrl = getAiApiBaseUrl();

  // Support multiple backend shapes (ollamaclient, direct Ollama proxy, etc.).
  try {
    const tagsRes = await fetch(`${baseUrl}/api/tags`, {signal});
    if (tagsRes.ok) return true;
  } catch {
    // Continue to fallback probes.
  }

  try {
    const healthRes = await fetch(`${baseUrl}/api/health`, {signal});
    if (healthRes.ok) return true;
  } catch {
    // Continue to fallback probes.
  }

  try {
    const genRes = await fetch(`${baseUrl}/api/generate`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        prompt: 'ping',
        stream: false,
      }),
      signal,
    });
    return genRes.ok;
  } catch {
    return false;
  }
}

export const AiAvailabilityProvider = ({children}) => {
  // If compile-time flag is off, skip probing and stay false.
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiChecking, setAiChecking] = useState(AI_FEATURES_ENABLED);

  const checkAvailability = useCallback(async () => {
    if (!AI_FEATURES_ENABLED) {
      setAiChecking(false);
      return;
    }
    setAiChecking(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    try {
      const available = await probeAiService(controller.signal);
      setAiAvailable(available);
    } finally {
      clearTimeout(timer);
      setAiChecking(false);
    }
  }, []);

  useEffect(() => {
    checkAvailability();
    const onFocus = () => checkAvailability();
    const onHostChanged = () => checkAvailability();
    window.addEventListener('focus', onFocus);
    window.addEventListener('ai-api-host-changed', onHostChanged);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ai-api-host-changed', onHostChanged);
    };
  }, [checkAvailability]);

  return (
    <AiAvailabilityContext.Provider
      value={{
        aiAvailable,
        aiChecking,
        refreshAiAvailability: checkAvailability,
      }}
    >
      {children}
    </AiAvailabilityContext.Provider>
  );
};
